import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BackIcon,
  ClipboardIcon,
  CheckIcon,
  DownloadIcon,
  QRCodeIcon,
  HomeIcon,
  CameraIcon,
  PlusIcon,
  UserIcon,
} from "../components/Icons";

// Use current origin to support multiple domains
const BASE_URL = window.location.origin;

// Base32 alphabet (no ambiguous characters)
const ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

function generateCode() {
  let code = "INV-";
  for (let i = 0; i < 8; i++) {
    code += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return code;
}

function QRCodes() {
  const navigate = useNavigate();
  const [countInput, setCountInput] = useState("12");
  // Clamp to the input's 1-100 range; blank/invalid falls back to 1
  const count = Math.min(100, Math.max(1, parseInt(countInput, 10) || 1));
  const [labelPrefix, setLabelPrefix] = useState("");
  const [urls, setUrls] = useState([]);
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const newUrls = [];

    // Parse starting number from prefix (e.g., "L-012" -> prefix "L-", start 12)
    let prefix = "";
    let startNum = 0;
    let numDigits = 0;

    if (labelPrefix.trim()) {
      const match = labelPrefix.match(/^(.+?)(\d+)$/);
      if (match) {
        prefix = match[1];
        startNum = parseInt(match[2], 10);
        numDigits = match[2].length;
      } else {
        // No number at end, just use as prefix with no numbering
        prefix = labelPrefix;
      }
    }

    if (prefix) {
      // Containers: 1 per line with label (URL,CODE,LABEL)
      for (let i = 0; i < count; i++) {
        const code = generateCode();
        const url = `${BASE_URL}/scan/${code}`;

        if (numDigits > 0) {
          const label = prefix + String(startNum + i).padStart(numDigits, "0");
          newUrls.push(`${url},${code},${label}`);
        } else {
          newUrls.push(`${url},${code},${prefix}`);
        }
      }
    } else {
      // Products: 2 per line, 4 columns (URL1,CODE1,URL2,CODE2)
      for (let i = 0; i < count; i += 2) {
        const code1 = generateCode();
        const url1 = `${BASE_URL}/scan/${code1}`;

        if (i + 1 < count) {
          const code2 = generateCode();
          const url2 = `${BASE_URL}/scan/${code2}`;
          newUrls.push(`${url1},${code1},${url2},${code2}`);
        } else {
          newUrls.push(`${url1},${code1},,`);
        }
      }
    }
    setUrls(newUrls);
    setCopied(false);
  };

  const handleDownloadCsv = () => {
    // Rows are already CSV: 3 columns for containers, 4 for products.
    const header =
      urls[0].split(",").length === 3
        ? "url,code,label"
        : "url_1,code_1,url_2,code_2";
    const csv = [header, ...urls].join("\n") + "\n";
    const blobUrl = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `qr-codes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  };

  const handleCopyAll = async () => {
    await navigator.clipboard.writeText(urls.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">QR Codes</h1>
            <p className="header-subtitle">
              Generate URLs for printing QR code labels
            </p>
          </div>
          {urls.length > 0 && (
            <div className="header-actions-right">
              <button
                onClick={handleCopyAll}
                className="btn btn-ghost btn-icon"
                title="Copy all"
              >
                {copied ? <CheckIcon size={20} /> : <ClipboardIcon size={20} />}
              </button>
              <button
                onClick={handleDownloadCsv}
                className="btn btn-ghost btn-icon"
                title="Export CSV"
              >
                <DownloadIcon size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
          <p
            className="text-secondary"
            style={{ marginBottom: "var(--spacing-md)" }}
          >
            Generate URLs to print as QR codes. When scanned, each opens the app
            to create a new item.
          </p>

          <div className="form-group">
            <label>Number of codes</label>
            <input
              type="number"
              min="1"
              max="100"
              value={countInput}
              onChange={(e) => setCountInput(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Label prefix (optional)</label>
            <input
              type="text"
              placeholder="e.g., L-001"
              value={labelPrefix}
              onChange={(e) => setLabelPrefix(e.target.value)}
            />
            <p
              className="text-secondary"
              style={{ fontSize: "0.85rem", marginTop: "var(--spacing-xs)" }}
            >
              If ending with a number, labels will increment (L-001, L-002...)
            </p>
          </div>

          <button
            onClick={handleGenerate}
            className="btn btn-primary"
            style={{ width: "100%" }}
          >
            Generate {count} URLs
          </button>
        </div>

        {urls.length > 0 && (
          <div className="card">
            <pre
              style={{
                margin: 0,
                padding: 0,
                fontFamily: "monospace",
                fontSize: "0.8rem",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {urls.join("\n")}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default QRCodes;
